import { useState, useEffect, lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import { authAPI, karmaAPI } from './lib/api';
import { applySessionRenewal } from './lib/sessionRenewal';
import { sessionEnded } from './lib/authFlow';
import { useAccessibilitySettings } from './hooks/useAccessibilitySettings';
import { useFavorites } from './hooks/useFavorites';
import { usePageMeta } from './hooks/usePageMeta';
import useNativeModalBridge from './lib/nativeModalBridge';
import CookieConsent from './components/CookieConsent';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { pathBehind } from './lib/scrollMemory';

// Lazy-loaded pages — only downloaded when the user navigates to them
const MapaVivo = lazy(() => import('./pages/MapaVivo'));
const Accesibilidad = lazy(() => import('./pages/Accesibilidad'));
const Boletines = lazy(() => import('./pages/Boletines'));
const Novedades = lazy(() => import('./pages/Novedades'));
const Parcela = lazy(() => import('./pages/Parcela'));
const Urbanismo = lazy(() => import('./pages/Urbanismo'));
const Vecinos = lazy(() => import('./pages/Vecinos'));
const VecinosDetail = lazy(() => import('./pages/VecinosDetail'));
const Servicios = lazy(() => import('./pages/Servicios'));
const Comercio = lazy(() => import('./pages/Comercio'));
const ComercioDetail = lazy(() => import('./pages/ComercioDetail'));
const Agenda = lazy(() => import('./pages/Agenda'));
const Deportes = lazy(() => import('./pages/Deportes'));
const Torrebus = lazy(() => import('./pages/Torrebus'));
const Torrebici = lazy(() => import('./pages/Torrebici'));
const Renfe = lazy(() => import('./pages/Renfe'));
const Aparcamiento = lazy(() => import('./pages/Aparcamiento'));
const Incidencias = lazy(() => import('./pages/Incidencias'));
const IncidenciaDetail = lazy(() => import('./pages/IncidenciaDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const Auth = lazy(() => import('./pages/Auth'));
const Legal = lazy(() => import('./pages/Legal'));
const Sobre = lazy(() => import('./pages/Sobre'));
const PreguntasFrecuentes = lazy(() => import('./pages/PreguntasFrecuentes'));
const Playas = lazy(() => import('./pages/Playas'));
const Montanas = lazy(() => import('./pages/Montanas'));
const QueVer = lazy(() => import('./pages/QueVer'));
const Patrimonio = lazy(() => import('./pages/Patrimonio'));
const Naturaleza = lazy(() => import('./pages/Naturaleza'));
const NotFound = lazy(() => import('./pages/NotFound'));
const EnCifras = lazy(() => import('./pages/EnCifras'));
const Residuos = lazy(() => import('./pages/Residuos'));
const Transparencia = lazy(() => import('./pages/Transparencia'));
const Contratacion = lazy(() => import('./pages/Contratacion'));
const Descargas = lazy(() => import('./pages/Descargas'));
const ContractDetail = lazy(() => import('./pages/ContractDetail'));
const CompanyProfile = lazy(() => import('./pages/CompanyProfile'));
const Radiografia = lazy(() => import('./pages/Radiografia'));
const Tramites = lazy(() => import('./pages/Tramites'));
const TramiteDetail = lazy(() => import('./pages/TramiteDetail'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CedulaBorrador = lazy(() => import('./pages/CedulaBorrador'));
const Trafico = lazy(() => import('./pages/Trafico'));
const Gasolineras = lazy(() => import('./pages/Gasolineras'));
const PrecioLuz = lazy(() => import('./pages/PrecioLuz'));
const PuntosCarga = lazy(() => import('./pages/PuntosCarga'));
const Tiempo = lazy(() => import('./pages/Tiempo'));
const ZBE = lazy(() => import('./pages/ZBE'));
const Festivos = lazy(() => import('./pages/Festivos'));
const Supermercados = lazy(() => import('./pages/Supermercados'));
const Farmacias = lazy(() => import('./pages/Farmacias'));
const Patrona = lazy(() => import('./pages/Patrona'));
const PatronaEvento = lazy(() => import('./pages/PatronaEvento'));
const Buscar = lazy(() => import('./pages/Buscar'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-torre-teal/30 border-t-torre-teal rounded-full animate-spin" />
        <p className="text-sm text-slate-400">Cargando...</p>
      </div>
    </div>
  );
}

// Keyed by path: after a crash or a failed chunk load, navigating elsewhere gets a fresh boundary
// instead of "Algo salió mal" on every later page (in the app there is no reload button).
function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

// Storage can be blocked (site data off, some private modes); reading it then throws, and up here
// that is a white page.
function readToken() {
  try { return localStorage.getItem('torre_token'); } catch { return null; }
}
function removeStored(...keys) {
  keys.forEach((k) => { try { localStorage.removeItem(k); } catch { /* storage unavailable */ } });
}

function PageMetaUpdater() {
  const location = useLocation();
  usePageMeta(location.pathname);
  return null;
}

// ⚠️ Deep Link Ping-Pong Handshake — See mobile/CRITICAL_FIXES.md #30
// Sends PING to native layer after React hydration, listens for queued deep links.
function DeepLinkHandler() {
  const navigate = useNavigate();
  useNativeModalBridge();

  useEffect(() => {
    if (!window.ReactNativeWebView) return; // Skip in browser

    const sendPing = () => {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'WEBVIEW_PING' }));
    };

    const handlePong = () => {};

    // The app's back button sends the previous path rather than stepping back. When that path is
    // the entry right behind this one, step back instead, so it is a real back: the reader returns
    // to where they were on that page (lib/scrollMemory). Anything else, such as
    // a tab or a notification, is a new visit as before.
    const handleDeepLink = (event) => {
      const path = event.detail?.path;
      if (!path) return;
      if (pathBehind(path)) navigate(-1);
      else navigate(path);
    };

    window.addEventListener('nativePong', handlePong);
    window.addEventListener('nativeDeepLink', handleDeepLink);

    // Send PING after React has fully rendered (300ms delay)
    const timer = setTimeout(sendPing, 300);

    // Retry PING if no PONG within 2s (resilience for slow Android devices)
    const retryTimer = setTimeout(() => {
      if (window.ReactNativeWebView) sendPing();
    }, 2000);

    return () => {
      clearTimeout(timer);
      clearTimeout(retryTimer);
      window.removeEventListener('nativePong', handlePong);
      window.removeEventListener('nativeDeepLink', handleDeepLink);
    };
  }, [navigate]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const openAuth = () => setShowAuth(true);
  const [loaded, setLoaded] = useState(() => !readToken());
  const { settings: a11y, update: updateA11y, toggleSeniorMode } = useAccessibilitySettings();
  const favoritesHook = useFavorites();

  useEffect(() => {
    // Clear PostHog cookies left on readers who accepted them while it was still loading.
    // PostHog is gone from public/index.html; this only tidies up after it.
    document.cookie.split(';').forEach(c => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('ph_')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });

    const checkAuth = (attempt = 0) => {
      const token = readToken();
      if (token) {
        authAPI.me()
          .then(res => {
            setUser(applySessionRenewal(res.data, token));
            karmaAPI.dailyLogin()
              .then(loginRes => {
                if (loginRes?.data?.login_streak) {
                  setUser(prev => prev ? { ...prev, login_streak: loginRes.data.login_streak, karma: loginRes.data.karma || prev.karma } : prev);
                }
              })
              .catch(() => {});
          })
          .catch((err) => {
            // Only the server saying the session is over (401: expired, revoked, unknown; 403:
            // suspended or unverified) signs out. Any failure did: a server restart (every deploy),
            // a dropped connection or a 5xx signed out whoever opened the app at that moment, and
            // inside the app the shell then forgot the token and unlinked the phone's push. Kept
            // otherwise, and asked again shortly.
            if (sessionEnded(err)) removeStored('torre_token');
            else if (attempt < 3) setTimeout(() => checkAuth(attempt + 1), [3000, 10000, 30000][attempt]);
          })
          .finally(() => setLoaded(true));
      } else {
        setUser(null);
        setLoaded(true);
      }
    };
    checkAuth();
    // Expose for native app bridge to trigger auth refresh without page reload
    window.__somostorre_auth_refresh = () => checkAuth();
    return () => { delete window.__somostorre_auth_refresh; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuth = (userData) => {
    setUser(userData);
    setShowAuth(false);
  };

  // Same as Perfil's «Cerrar sesión» (pages/Profile.js) and the app's LOGOUT: in the app the
  // shell clears its stored token and reloads; on the web the token and the favourites go.
  const handleLogout = () => {
    setUser(null);
    setShowAuth(false);
    if (window.__isNativeApp && window.__logout) {
      window.__logout();
      return;
    }
    removeStored('torre_token', 'torre_favorites');
    window.location.href = '/';
  };

  if (!loaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <svg viewBox="0 0 42 30" width={48} height={34} fill="none" className="text-torre-teal animate-pulse" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="7" width="8" height="23" rx="1" fill="currentColor"/>
            <polygon points="6,0 1,7 11,7" fill="currentColor"/>
            <rect x="3.5" y="11" width="2" height="2.5" rx="0.5" fill="white" opacity="0.2"/>
            <circle cx="6" cy="-1" r="1.8" fill="#D83A3A"/>
            <path d="M10 30 Q20 18 30 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.3"/>
            <rect x="30" y="12" width="10" height="18" rx="1" fill="currentColor" opacity="0.6"/>
            <ellipse cx="35" cy="12" rx="5.5" ry="4.5" fill="currentColor" opacity="0.6"/>
          </svg>
          <p className="text-sm text-slate-500 font-medium">Cargando SomosTorre...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <PageMetaUpdater />
      <DeepLinkHandler />
      <Routes>
        <Route path="/admin" element={
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <AdminDashboard user={user} />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/admin/cedula/:ref" element={
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <CedulaBorrador user={user} />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="*" element={
          <Layout user={user} onLogout={handleLogout} onShowAuth={() => openAuth()} seniorMode={a11y.seniorMode} toggleSeniorMode={toggleSeniorMode} a11y={a11y} updateA11y={updateA11y}>
            <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Dashboard user={user} onShowAuth={() => openAuth()} />} />
              <Route path="/mapa" element={<MapaVivo user={user} />} />
              <Route path="/boletines" element={<Boletines user={user} />} />
              <Route path="/novedades" element={<Novedades />} />
              <Route path="/vecinos" element={<Vecinos user={user} onShowAuth={() => openAuth()} />} />
              <Route path="/vecinos/:id" element={<VecinosDetail user={user} onShowAuth={() => openAuth()} />} />
              <Route path="/servicios" element={<Servicios />} />
              <Route path="/comercio" element={<Comercio favorites={favoritesHook} />} />
              <Route path="/comercio/:id" element={<ComercioDetail />} />
              <Route path="/supermercados" element={<Supermercados />} />
              <Route path="/farmacias" element={<Farmacias />} />
              <Route path="/torrebus" element={<Torrebus favorites={favoritesHook} />} />
              <Route path="/torrebici" element={<Torrebici />} />
              <Route path="/renfe" element={<Renfe />} />
              <Route path="/aparcamiento" element={<Aparcamiento />} />
              <Route path="/incidencias" element={<Incidencias user={user} onShowAuth={() => openAuth()} />} />
              <Route path="/incidencias/:id" element={<IncidenciaDetail user={user} onShowAuth={() => openAuth()} />} />
              <Route path="/agenda" element={<Agenda />} />
              <Route path="/agenda/:id" element={<EventDetail />} />
              <Route path="/deportes" element={<Deportes />} />
              <Route path="/patrona" element={<Patrona />} />
              <Route path="/patrona/*" element={<PatronaEvento />} />
              <Route path="/perfil" element={<Profile user={user} onUserUpdate={setUser} onShowAuth={() => openAuth()} />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/accesibilidad" element={<Accesibilidad user={user} />} />
              {/* Old verification links (password sign-up, retired 2026-09-26; Android still opens
                  /verificar in the app). Auth sees ?verification= and says how to get in now. */}
              <Route path="/verificar" element={<Navigate to="/?verification=retirada" replace />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/preguntas-frecuentes" element={<PreguntasFrecuentes />} />
              <Route path="/playas" element={<Playas />} />
              <Route path="/montanas" element={<Montanas />} />
              <Route path="/tiempo" element={<Tiempo />} />
              <Route path="/zbe" element={<ZBE />} />
              <Route path="/festivos" element={<Festivos />} />
              <Route path="/parcela" element={<Parcela />} />
              <Route path="/parcela/:ref" element={<Parcela />} />
              <Route path="/urbanismo" element={<Urbanismo />} />
              <Route path="/que-ver" element={<QueVer />} />
              <Route path="/patrimonio" element={<Patrimonio />} />
              {/* same hub as /visitar; one canonical URL for search engines */}
              <Route path="/naturaleza" element={<Navigate to="/visitar" replace />} />
              <Route path="/visitar" element={<Naturaleza />} />
              <Route path="/en-cifras" element={<EnCifras />} />
              <Route path="/residuos" element={<Residuos />} />
              <Route path="/transparencia" element={<Transparencia />} />
              <Route path="/contratacion" element={<Contratacion onShowAuth={() => openAuth()} />} />
              <Route path="/descargas" element={<Descargas />} />
              <Route path="/contratacion/empresa/:name" element={<CompanyProfile />} />
              <Route path="/contratacion/empresa" element={<CompanyProfile />} />
              <Route path="/contratacion/:id" element={<ContractDetail />} />
              <Route path="/radiografia" element={<Radiografia />} />
              <Route path="/tramites" element={<Tramites user={user} />} />
              <Route path="/tramites/:id" element={<TramiteDetail user={user} />} />
              <Route path="/trafico" element={<Trafico />} />
              <Route path="/gasolineras" element={<Gasolineras />} />
              <Route path="/precio-luz" element={<PrecioLuz />} />
              <Route path="/puntos-carga" element={<PuntosCarga />} />
              <Route path="/buscar" element={<Buscar />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
          </Layout>
        } />
      </Routes>
      <CookieConsent />
      <Suspense fallback={null}>
        <Auth open={showAuth} onClose={() => setShowAuth(false)} onAuth={handleAuth} signedIn={!!user} />
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
